<script>
    import app from "@/store/app";
    import sosMode from "@/store/sosMode";
    import user from "@/store/user";
    import Icon from "@iconify/svelte";
    import { fly, fade } from "svelte/transition";
    import { cubicOut } from "svelte/easing";
    import { routesPOS } from "@/assets/js/options";
    import { onMount } from "svelte";
    import Dialog from "%/Dialog.svelte";
    import HubPinModal from "#/modals/HubPinModal.svelte";

    let hubPinDialog;
    let hubPinModal;
    let url = new URL(window.location);
    let isAdmin = url.pathname.split("/")[1] == "admin";

    const sosHiddenRoutes = ["/shifts", "/delivery", "/schedule", "/members", "/unpaid-orders", "/pending-orders"];
    // Routes that must stay in the same tab (client-side nav) so the live POS
    // store survives the trip — Pending Orders hands its order to /pos in memory.
    const sameTabRoutes = ["/pending-orders"];
    const sosOnlyRoutes = ["/sale-history"];

    $: menuRoutes = routesPOS.filter((r) => {
        if (r.route === "/admin-login") return false;
        if ($sosMode.enabled && sosHiddenRoutes.includes(r.route)) return false;
        if (!$sosMode.enabled && sosOnlyRoutes.includes(r.route)) return false;
        return true;
    });

    const closeDrawer = () => {
        $app.drawerPOS = false;
    };

    const openHubPin = () => {
        closeDrawer();
        hubPinModal?.reshuffle();
        hubPinDialog?.show();
    };

    const onHubPinSuccess = () => {
        hubPinDialog?.close();
        $app.url = "/hub";
    };

    const onHubPinClose = () => {
        hubPinDialog?.close();
    };

    function handleKeydown(e) {
        if (e.key === "Escape" && $app.drawerPOS) closeDrawer();
    }

    onMount(() => {
        closeDrawer();
    });
</script>

<svelte:window on:keydown={handleKeydown} />

{#if $app.drawerPOS}
    <div class="cd-backdrop" transition:fade={{ duration: 220 }} on:click={closeDrawer} aria-hidden="true"></div>

    <aside
        class="cd-panel"
        transition:fly={{ x: -480, duration: 420, easing: cubicOut }}
        role="dialog"
        aria-modal="true"
        aria-label="POS menu"
    >
        <header class="cd-head">
            <div class="cd-title-wrap">
                <h2 class="cd-title">Menu</h2>
            </div>
            <button class="cd-close" on:click={closeDrawer} aria-label="Close menu">
                <Icon icon="mdi:close" width="22" />
            </button>
        </header>

        <div class="cd-items">
            {#each menuRoutes as item}
                <a
                    href={item.route}
                    target={isAdmin || sameTabRoutes.includes(item.route) ? "" : "_blank"}
                    class="cd-row"
                    on:click={closeDrawer}
                >
                    <div class="cd-thumb">
                        <Icon icon={item.icon} width="30" />
                    </div>
                    <div class="cd-info">
                        <span class="cd-name">{item.name}</span>
                    </div>
                    <Icon icon="fluent:chevron-right-16-regular" width="18" class="cd-chevron" />
                </a>
            {/each}

            {#if !$sosMode.enabled}
                <button type="button" class="cd-row" on:click={openHubPin}>
                    <div class="cd-thumb cd-thumb-avatar">
                        {($user.email || $user.name || "?")[0].toUpperCase()}
                    </div>
                    <div class="cd-info">
                        <span class="cd-name">Hub</span>
                    </div>
                    <Icon icon="fluent:chevron-right-16-regular" width="18" class="cd-chevron" />
                </button>
            {/if}
        </div>
    </aside>
{/if}

<Dialog bind:this={hubPinDialog} dialogClass="hub-pin-dialog">
    <HubPinModal bind:this={hubPinModal} on:success={onHubPinSuccess} on:close={onHubPinClose} />
</Dialog>

<style>
    .cd-backdrop {
        position: fixed;
        inset: 0;
        z-index: 60;
        background: rgba(5, 6, 4, 0.6);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);
    }

    .cd-panel {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        z-index: 61;
        width: min(420px, 100vw);
        display: flex;
        flex-direction: column;
        background: var(--jh-bg, #0a0a0a);
        border-right: 1px solid var(--jh-line);
        box-shadow: 24px 0 60px rgba(0, 0, 0, 0.55);
        color: var(--jh-text);
    }

    .cd-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.15rem 1.3rem;
        border-bottom: 1px solid var(--jh-line);
    }

    .cd-title-wrap {
        display: flex;
        align-items: baseline;
        gap: 0.6rem;
    }

    .cd-title {
        font-family: var(--jh-font-display);
        font-size: 1.45rem;
        font-weight: 600;
        letter-spacing: -0.01em;
        margin: 0;
    }

    .cd-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 50%;
        border: 1px solid var(--jh-line-strong);
        background: none;
        color: var(--jh-text);
        cursor: pointer;
        transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
    }

    .cd-close:hover {
        border-color: var(--jh-accent);
        color: var(--jh-accent);
        transform: rotate(90deg);
    }

    .cd-items {
        flex: 1;
        overflow-y: auto;
        padding: 1rem 1.3rem;
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        overscroll-behavior: contain;
    }

    .cd-row {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.9rem;
        padding: 0.85rem;
        background: var(--jh-surface);
        border: 1px solid var(--jh-line);
        border-radius: var(--jh-radius);
        color: var(--jh-text);
        text-decoration: none;
        cursor: pointer;
        width: 100%;
        text-align: left;
        font: inherit;
        animation: cd-row-in 0.35s var(--jh-ease, ease) both;
        transition: border-color 0.2s ease, transform 0.2s ease;
    }

    .cd-row:hover {
        border-color: var(--jh-accent);
        transform: translateX(-2px);
    }

    @keyframes cd-row-in {
        from {
            opacity: 0;
            transform: translateX(16px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    .cd-thumb {
        flex: 0 0 48px;
        width: 48px;
        height: 48px;
        border-radius: 12px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        background: radial-gradient(80% 80% at 50% 30%, #1c1c1c, var(--jh-surface));
        color: var(--jh-accent);
    }

    .cd-thumb-avatar {
        font-size: 1.2rem;
        font-weight: 600;
        background: var(--jh-surface);
    }

    .cd-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    .cd-name {
        font-weight: 700;
        font-size: 0.98rem;
        line-height: 1.25;
    }

    :global(.cd-row .cd-chevron) {
        color: var(--jh-muted);
        flex: 0 0 auto;
    }

    :global(dialog.hub-pin-dialog) {
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: linear-gradient(165deg, #1e1e22 0%, #121214 100%);
        box-shadow:
            0 24px 64px rgba(0, 0, 0, 0.55),
            0 0 0 1px rgba(91, 225, 230, 0.08) inset;
        padding: 28px 24px 24px;
        max-width: 380px;
    }

    :global(dialog.hub-pin-dialog > div) {
        gap: 0;
    }

    @media (prefers-reduced-motion: reduce) {
        .cd-row {
            animation: none;
        }
    }
</style>
